import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Callout,
  Controller,
  DatePicker,
  Delimiter,
  Input,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { Laptop } from '@otp/icons';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import {
  CreateLmsCurriculumDto,
  LmsCurriculumMode,
  UpdateLmsCurriculumDto,
} from 'src/graphql/generated/graphql';
import { DocumentNode } from '@apollo/client';
import {
  mapFormDataToLmsCurriculumDto,
  mapLmsCurriculumToFormData,
} from './utils';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';
import { useLmsCurriculum } from 'src/hooks/query/lms/curriculums/use-curriculum';
import { useLmsCurriculumCreate } from 'src/hooks/query/lms/curriculums/use-curriculum-create';
import { useLmsCurriculumUpdate } from 'src/hooks/query/lms/curriculums/use-curriculum-update';
import { Form } from './types';
import clsx from 'clsx';
import { UserLevelField } from './fields/user-level';
import { ModeField } from './fields/mode';
import { CertificatesField } from './fields/certificates';
import { ProgramsField } from 'src/components/lms/curriculums/form/fields/programs';
import {
  RequiredCertificatesConfigField,
  validateDropdownConditionalCondition,
} from 'src/components/lms/curriculums/form/fields/required-certificates-config';
import { useErrorContext } from 'src/context/error.context';

type LmsCurriculumFormProps = {
  onFinish?: () => void;
  id?: string;
  refetchQueries?: DocumentNode[];
  redirectToListAfterSubmit?: boolean;
};

export const LmsCurriculumForm = ({
  refetchQueries,
  onFinish,
  id,
  redirectToListAfterSubmit = true,
}: LmsCurriculumFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'lms.curriculum.form',
  });
  const { entity } = useLmsCurriculum({ id: id });
  const [create] = useLmsCurriculumCreate(refetchQueries);
  const [update] = useLmsCurriculumUpdate(refetchQueries);
  const navigate = useNavigate();
  const { onServerError } = useErrorContext();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setValue,
    setError,
  } = useForm<Form>();

  const mode = watch('mode');
  const programId = watch('programId');

  useEffect(() => {
    if (!entity) {
      return;
    }
    const formData = mapLmsCurriculumToFormData(entity as any);
    reset(formData);
  }, [entity, reset]);

  useEffect(() => {
    if (programId && entity?.program?.id !== programId) {
      setValue('certificateIds', []);
      setValue('requiredCertificatesConfig', []);
    }
  }, [programId]);

  const validate = (data: Form) => {
    const isValid = validateDropdownConditionalCondition(
      data.requiredCertificatesConfig,
    );
    if (!isValid) {
      setError('requiredCertificatesConfig', {
        type: 'manual',
        message: t('fields.required-certificates-error'),
      });
    }
    return isValid;
  };

  const onSave = async (data: Form) => {
    if (!validate(data)) {
      return;
    }

    const entityToUpdate = mapFormDataToLmsCurriculumDto({
      form: data,
    });

    try {
      if (entity?.id) {
        await update({
          variables: {
            id: entity.id,
            data: entityToUpdate as UpdateLmsCurriculumDto,
          },
        });
      } else {
        await create({
          variables: {
            data: entityToUpdate as CreateLmsCurriculumDto,
          },
        });
      }

      if (redirectToListAfterSubmit) {
        navigate(Routes.lmsCurriculumsAdmin.path);
      }

      onFinish?.();
    } catch (e: any) {
      const isHandled = onServerError(e);

      if (!isHandled) {
        throw e;
      }
    }
  };

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSave)}>
        <TableWrapper>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary.10"
            className="flex items-center uppercase"
          >
            <Laptop className="mr-2" />
            {t(id ? 'title-edit' : 'title-add-new')}
          </Typography>
          {entity && (
            <Callout severity="info" className={clsx('mt-2', 'mb-2')}>
              {t('edit')}
            </Callout>
          )}
          <div className={clsx('mb-4', 'grid', 'md:grid-cols-12', 'gap-2')}>
            <div className="col-span-8">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    label={t('fields.name')}
                    placeholder={t('fields.name')}
                    error={!!errors.name}
                    required={true}
                    {...field}
                    defaultValue={field.value}
                  />
                )}
              />
            </div>
            <div className="col-span-4">
              <Controller
                name="programId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div>
                    <Typography
                      variant="subtitle1"
                      color={!!errors.mode ? 'red' : ''}
                    >
                      {t('fields.program')}
                      <span className="text-red-500"> *</span>
                    </Typography>
                    <ProgramsField
                      field={field}
                      hasError={!!errors.programId}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className={clsx('mb-4', 'grid', 'md:grid-cols-3', 'gap-2')}>
            <Controller
              name="externalId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('fields.externalId')}
                  placeholder={t('fields.externalId')}
                  error={!!errors.externalId}
                  required={true}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
            <Controller
              name="estimatedTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('fields.estimatedTime')}
                  placeholder={t('fields.estimatedTime')}
                  error={!!errors.estimatedTime}
                  required={true}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
            <Controller
              name="cost"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Input
                  label={t('fields.cost')}
                  placeholder={t('fields.cost')}
                  error={!!errors.cost}
                  required={false}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
          </div>
          <div className={clsx('mb-4', 'grid', 'grid-cols-3', 'gap-2')}>
            <Controller
              name="mode"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    variant="subtitle1"
                    color={!!errors.mode ? 'red' : ''}
                  >
                    {t('fields.mode')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <ModeField field={field} hasError={!!errors.mode} />
                </div>
              )}
            />
            {mode === LmsCurriculumMode.InPerson && (
              <Controller
                name="startsAt"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div>
                    <Typography
                      variant="subtitle1"
                      color={!!errors.startsAt ? 'red' : ''}
                    >
                      {t('fields.startsAt')}
                    </Typography>
                    <DatePicker
                      {...field}
                      className="w-full"
                      defaultValue={field.value}
                      disablePast
                    />
                  </div>
                )}
              />
            )}
            {mode === LmsCurriculumMode.InPerson && (
              <Controller
                name="finishesAt"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div>
                    <Typography
                      variant="subtitle1"
                      color={!!errors.finishesAt ? 'red' : ''}
                    >
                      {t('fields.finishesAt')}
                    </Typography>
                    <DatePicker
                      {...field}
                      className="w-full"
                      defaultValue={field.value}
                      disablePast
                    />
                  </div>
                )}
              />
            )}
          </div>
          <div className="mb-4">
            <Controller
              name="description"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.description ? 'red' : ''}
                  >
                    {t('fields.description')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="link"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('fields.link')}
                  placeholder={t('fields.link')}
                  error={!!errors.link}
                  required={true}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
          </div>
        </TableWrapper>

        <TableWrapper className="mt-4">
          <div className={clsx('mb-4', 'grid', 'md:grid-cols-2', 'gap-2')}>
            <Controller
              name="changesLevelTo"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography
                    variant="subtitle1"
                    color={!!errors.changesLevelTo ? 'red' : ''}
                  >
                    {t('fields.changesLevelTo')}
                  </Typography>
                  <UserLevelField
                    field={field}
                    hasError={!!errors.changesLevelTo}
                  />
                </div>
              )}
            />
            <Controller
              name="certificateIds"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="flex-1">
                  <Typography
                    variant="subtitle1"
                    color={!!errors.certificateIds ? 'red' : ''}
                  >
                    {t('fields.certificateIds')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <CertificatesField
                    field={field}
                    placeholder={t('fields.certificateIds')}
                    programId={programId}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="requiredCertificatesConfig"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div className="flex-1">
                  <Typography
                    variant="subtitle1"
                    color={!!errors.requiredCertificatesConfig ? 'red' : ''}
                  >
                    {t('fields.requiredCertificateIds')}
                  </Typography>
                  <RequiredCertificatesConfigField
                    field={field}
                    programId={programId}
                    error={errors.requiredCertificatesConfig?.message as string}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="adminNotes"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.adminNotes ? 'red' : ''}
                  >
                    {t('fields.adminNotes')}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
        </TableWrapper>

        <Delimiter />
        <div className="flex justify-end space-x-2 mt-2">
          <Button variant="contained" type="submit">
            {t('save')}
          </Button>
        </div>
      </form>
    </ContentWrapper>
  );
};

export default LmsCurriculumForm;
